import { Wallet, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { BUDGET_MONTH_HEADERS } from "~/lib/contabilidad/generateBudgetTemplate";
import { parseGridCellValue } from "~/lib/contabilidad/parseBudgetFile";

import type { Account } from "~/types/contabilidad";

interface BudgetGridProps {
  accounts: Account[];
  // Raw cell strings per account (12 values, Ene..Dic)
  values: Record<string, string[]>;
  onChangeCell: (accountId: string, monthIndex: number, value: string) => void;
  onRemoveAccount: (accountId: string) => void;
  disabled: boolean;
}

function formatAmount(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function rowTotal(cells: string[]): number {
  return cells.reduce((sum, cell) => sum + parseGridCellValue(cell), 0);
}

function sectionTotals(accounts: Account[], values: Record<string, string[]>) {
  const byMonth = Array(12).fill(0) as number[];
  accounts.forEach((account) => {
    const cells = values[account.id] || [];
    for (let month = 0; month < 12; month++) {
      byMonth[month] += parseGridCellValue(cells[month] ?? "");
    }
  });
  return { byMonth, total: byMonth.reduce((sum, value) => sum + value, 0) };
}

interface SectionProps {
  title: string;
  accounts: Account[];
  values: Record<string, string[]>;
  onChangeCell: BudgetGridProps["onChangeCell"];
  onRemoveAccount: BudgetGridProps["onRemoveAccount"];
  disabled: boolean;
}

function GridSection({
  title,
  accounts,
  values,
  onChangeCell,
  onRemoveAccount,
  disabled,
}: SectionProps) {
  if (accounts.length === 0) return null;

  const totals = sectionTotals(accounts, values);

  return (
    <>
      <TableRow className="bg-muted/60 hover:bg-muted/60">
        <TableCell
          colSpan={15}
          className="sticky left-0 bg-muted/60 py-2 font-bold tracking-wide"
        >
          {title}
        </TableCell>
      </TableRow>
      {accounts.map((account) => {
        const cells = values[account.id] || Array(12).fill("");
        return (
          <TableRow key={account.id} className="hover:bg-muted/30">
            <TableCell className="sticky left-0 z-10 min-w-[240px] bg-card py-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-mono text-sm text-muted-foreground">
                    {account.code}
                  </span>
                  <span className="ml-2 text-sm">{account.name}</span>
                </div>
                {!disabled && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-muted-foreground"
                    onClick={() => onRemoveAccount(account.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
            {cells.map((cell, monthIndex) => (
              <TableCell key={monthIndex} className="p-1">
                <Input
                  value={cell}
                  onChange={(e) =>
                    onChangeCell(account.id, monthIndex, e.target.value)
                  }
                  disabled={disabled}
                  inputMode="numeric"
                  className="h-9 w-28 text-right font-mono text-sm"
                  placeholder="0"
                />
              </TableCell>
            ))}
            <TableCell className="min-w-[120px] text-right font-mono text-sm text-muted-foreground">
              {formatAmount(rowTotal(cells))}
            </TableCell>
          </TableRow>
        );
      })}
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell className="sticky left-0 z-10 bg-muted/30 py-2 font-semibold">
          Total {title}
        </TableCell>
        {totals.byMonth.map((value, monthIndex) => (
          <TableCell
            key={monthIndex}
            className="text-right font-mono text-sm font-semibold"
          >
            {formatAmount(value)}
          </TableCell>
        ))}
        <TableCell className="text-right font-mono text-sm font-semibold">
          {formatAmount(totals.total)}
        </TableCell>
      </TableRow>
    </>
  );
}

export function BudgetGrid({
  accounts,
  values,
  onChangeCell,
  onRemoveAccount,
  disabled,
}: BudgetGridProps) {
  if (accounts.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Wallet}
          title="Sin cuentas presupuestadas"
          description='Agrega cuentas con "Agregar Cuentas" o importa un archivo Excel para comenzar.'
        />
      </div>
    );
  }

  const ingresos = accounts.filter((a) => a.account_type === "ingreso");
  const gastos = accounts.filter((a) => a.account_type === "gasto");

  const ingresosTotals = sectionTotals(ingresos, values);
  const gastosTotals = sectionTotals(gastos, values);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 z-10 min-w-[240px] bg-card">
              Cuenta
            </TableHead>
            {BUDGET_MONTH_HEADERS.map((month) => (
              <TableHead key={month} className="min-w-[116px] text-right">
                {month}
              </TableHead>
            ))}
            <TableHead className="min-w-[120px] text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <GridSection
            title="Ingresos"
            accounts={ingresos}
            values={values}
            onChangeCell={onChangeCell}
            onRemoveAccount={onRemoveAccount}
            disabled={disabled}
          />
          <GridSection
            title="Costos y Gastos"
            accounts={gastos}
            values={values}
            onChangeCell={onChangeCell}
            onRemoveAccount={onRemoveAccount}
            disabled={disabled}
          />
          <TableRow className="border-t-2 bg-muted/50 hover:bg-muted/50">
            <TableCell className="sticky left-0 z-10 bg-muted/50 py-2.5 font-bold tracking-wide">
              Resultado
            </TableCell>
            {Array.from({ length: 12 }, (_, monthIndex) => {
              // Gastos are stored signed (negative), so the result is the
              // algebraic sum, matching the estado de resultados.
              const value =
                ingresosTotals.byMonth[monthIndex] +
                gastosTotals.byMonth[monthIndex];
              return (
                <TableCell
                  key={monthIndex}
                  className={`text-right font-mono text-sm font-bold ${
                    value < 0 ? "text-red-700" : "text-green-700"
                  }`}
                >
                  {formatAmount(value)}
                </TableCell>
              );
            })}
            <TableCell
              className={`text-right font-mono text-sm font-bold ${
                ingresosTotals.total + gastosTotals.total < 0
                  ? "text-red-700"
                  : "text-green-700"
              }`}
            >
              {formatAmount(ingresosTotals.total + gastosTotals.total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
